// FTEDN Team - Feedback Intelligence Page
import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import './FeedbackIntel.css';

interface FeedbackItem {
  id: string;
  creatorName?: string;
  creatorEmail?: string;
  userName?: string;
  userId?: string;
  programName?: string;
  productName?: string;
  type?: string;
  content?: string;
  comment?: string;
}

interface ReviewItem {
  id: string;
  creatorName?: string;
  creatorEmail?: string;
  userName?: string;
  userId?: string;
  productName?: string;
  programName?: string;
  rating?: number;
  overallRating?: number;
  title?: string;
  content?: string;
  comment?: string;
  notes?: string;
}

function getItems<T>(response: unknown): T[] {
  const payload = response as { data?: T[] };
  return Array.isArray(payload?.data) ? payload.data : [];
}

function creatorLabel(item: Pick<FeedbackItem, 'creatorName' | 'creatorEmail' | 'userName' | 'userId'>) {
  return item.creatorName || item.userName || item.creatorEmail || item.userId || 'Unknown creator';
}

function productLabel(item: Pick<FeedbackItem, 'productName' | 'programName'>) {
  return item.productName || item.programName || 'Unassigned product';
}

function secondaryProgramLabel(item: Pick<FeedbackItem, 'productName' | 'programName'>) {
  return item.productName && item.programName && item.productName !== item.programName ? item.programName : '';
}

function reviewRatingLabel(review: ReviewItem) {
  const rating = review.rating ?? review.overallRating;
  return rating ? `${rating}/5` : 'unrated';
}

export function FeedbackIntelPage() {
  const { get } = useApi();
  const [feedback, setFeedback] = useState<FeedbackItem[]>([]);
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadIntel = useCallback(async () => {
    setError('');
    try {
      const [feedbackResponse, reviewsResponse] = await Promise.all([
        get('/feedback'),
        get('/feedback/reviews'),
      ]);
      setFeedback(getItems<FeedbackItem>(feedbackResponse));
      setReviews(getItems<ReviewItem>(reviewsResponse));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feedback intelligence');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    void loadIntel();
  }, [loadIntel]);

  return (
    <div className="mgmt-page">
      <div className="mgmt-header">
        <h1>Feedback Intelligence</h1>
        <p className="mgmt-subtitle">All creator feedback compiled for analysis</p>
        <div className="mgmt-stats">
          <span className="stat-chip">{feedback.length} Feedback</span>
          <span className="stat-chip approved">{reviews.length} Reviews</span>
        </div>
      </div>
      {error && <div className="mgmt-error" role="alert">{error}</div>}
      {loading && <div className="mgmt-empty">Loading feedback...</div>}
      {!loading && (
        <>
          <h2 className="section-title">Feedback</h2>
          <div className="feedback-list">
            {feedback.length === 0 && <div className="mgmt-empty">No feedback yet.</div>}
            {feedback.map((item) => (
              <div key={item.id} className="feedback-row">
                <div className="feedback-row-main">
                  <div className="feedback-row-meta">
                    <strong>{creatorLabel(item)}</strong>
                    <span className="feedback-product">{productLabel(item)}</span>
                    <span className="feedback-type">{item.type || 'feedback'}</span>
                  </div>
                  {secondaryProgramLabel(item) && (
                    <span className="feedback-context">{secondaryProgramLabel(item)}</span>
                  )}
                </div>
                <p className="feedback-row-content">{item.content || item.comment || 'No feedback text'}</p>
              </div>
            ))}
          </div>
          <h2 className="section-title">Reviews</h2>
          <div className="feedback-list">
            {reviews.length === 0 && <div className="mgmt-empty">No reviews yet.</div>}
            {reviews.map((review) => (
              <div key={review.id} className="feedback-row">
                <div className="feedback-row-main">
                  <div className="feedback-row-meta">
                    <strong>{creatorLabel(review)}</strong>
                    <span className="feedback-product">{review.title || productLabel(review)}</span>
                    <span className="feedback-rating">{reviewRatingLabel(review)}</span>
                  </div>
                  {review.title && (review.productName || review.programName) && (
                    <span className="feedback-context">{productLabel(review)}</span>
                  )}
                </div>
                <p className="feedback-row-content">{review.comment || review.content || review.notes || 'No review text'}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
